import { RouteComponentProps } from 'react-router-dom';
import { getPageStatus, PageLayout } from 'components/PageLayout';
import { canReportGeoEventType, useAuthState } from 'state/useAuthState';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Report } from 'features/reports/report.types';
import { ReportableGeoEvent } from 'features/reports/components/ReportForm/types';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { useReport } from 'features/reports/hooks/useReport';
import { EditReportForm } from './EditReportForm';

type ContentProps = {
  geoEvent: ReportableGeoEvent;
  report: Report;
  onUpdateReport: (params: Partial<Report>) => Promise<Report>;
  isReportUpdating: boolean;
};

const Content = (props: ContentProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('editReport.title')}</title>
      </Helmet>
      <EditReportForm {...props} />
    </>
  );
};

export const EditReport = (
  props: RouteComponentProps<{ geoEventId: string; reportId: string }>,
): JSX.Element => {
  const {
    match: {
      params: { geoEventId, reportId },
    },
  } = props;
  const { t } = useTranslation();
  const { permissions } = useAuthState();
  const {
    report,
    isReportInError,
    isReportLoading,
    isReportUpdating,
    onUpdateReport,
  } = useReport(Number(reportId));

  const geoEventQuery = useGeoEvent<ReportableGeoEvent>({
    id: Number(geoEventId),
    useCacheBuster: true,
  });

  const isLoading = geoEventQuery.isGeoEventLoading || isReportLoading;
  const isError = geoEventQuery.isGeoEventFetchError || isReportInError;

  const status = getPageStatus({
    isUnauthorized: geoEventQuery?.geoEvent
      ? !canReportGeoEventType(permissions, geoEventQuery.geoEvent.geoEventType)
      : undefined,
    isLoading,
    isError: isError || !geoEventQuery.geoEvent || !report,
  });

  return (
    <PageLayout
      data-cname="AddIncidentReportPage"
      status={status}
      headerBarProps={{
        action: 'back',
        title: t('editReport.title'),
      }}
    >
      {geoEventQuery.geoEvent && report && (
        <Content
          geoEvent={geoEventQuery.geoEvent}
          report={report}
          onUpdateReport={onUpdateReport}
          isReportUpdating={isReportUpdating}
        />
      )}
    </PageLayout>
  );
};
